import { useState, useEffect, useRef } from 'react'
import { Box, LinearProgress, Card, Typography } from '@material-ui/core'
import moment from 'moment'
import HourlyAverageChart from './components/HourlyAverageChart'
import HourDetailModal from './components/HourDetailModal'
import SeparatorDetailModal from './components/SeparatorDetailModal'
import ItemsPerHourChart from './components/ItemsPerHourChart'
import { usePopup } from 'hooks/usePopup'

import { useStyles } from './styles'
import TimePerItem from './components/TimePerItem'
import ItemsPerSeparator from './components/ItemsPerSeparator'
import { IGetAverageTimeItemSeparators } from 'interfaces/ISeparator'
import { separatorService } from 'services/separatorService'

interface IData {
  username: string
  average_item_separate: number
  total_items?: number
  isAboveAverage?: boolean
}

interface ITimeFilteredData {
  globalAverage: number
  totalItems: number
  totalUsers: number
  totalHours: number
  items_per_hour: number
  users: IData[]
}

interface ITimelineEntry {
  separated_hour: string
  total_items: number
  total_secs: number
  avg_secs: number
  users: Array<{
    separator_id: number
    total_items: number
    total_secs: number
    avg_secs: number
    username: string
  }>
}

interface IHourData {
  hour: string
  users: Array<{
    separator_id: number
    total_items: number
    total_secs: number
    avg_secs: number
    username: string
  }>
  avg_secs: number
  total_items: number
  total_secs: number
}

interface ISeparatorData {
  username: string
  avg_secs: number
  total_items: number
  total_secs: number
  timeline: Array<{
    separated_hour: string
    avg_secs: number
    global_avg: number
    total_items: number
    total_secs: number
  }>
}

interface Props {
  scheduledDateGte: Date
  scheduledDateLte: Date
  selectedGroupId?: number
  refreshTrigger?: number
  separationMethod?: string
}

const AverageTimeItem = (props: Props) => {
  const { scheduledDateGte, scheduledDateLte, selectedGroupId, refreshTrigger, separationMethod } = props
  const [data, setData] = useState<IData[]>([])
  const [noData, setNoData] = useState(false)
  const [AverageTimeItem, setAverageTimeItem] = useState<number>(0)
  const [averageItems, setAverageItems] = useState<number>(0)
  const [loading, setLoading] = useState(false)
  const [metricsData, setMetricsData] = useState<ITimeFilteredData | null>(null)
  const [timelineData, setTimelineData] = useState<ITimelineEntry[]>([])
  const [modalHourData, setModalHourData] = useState<IHourData | null>(null)
  const [modalSeparatorData, setModalSeparatorData] = useState<ISeparatorData | null>(null)
  const classes = useStyles()
  const { addPopup } = usePopup()
  const controllerRef = useRef<AbortController | null>(null)

  const handleSeparatorClick = (separatorData: {
    username: string
    avg_secs: number
    total_items: number
    total_secs: number
    timeline: any[]
  }) => {
    // Encontrar os dados da timeline para este separador
    const separatorTimeline = timelineData.map(entry => {
      const userData = entry.users.find(user => user.username === separatorData.username)
      return {
        separated_hour: entry.separated_hour,
        avg_secs: userData?.avg_secs || 0,
        global_avg: entry.avg_secs, // Média global do horário
        total_items: userData?.total_items || 0,
        total_secs: userData?.total_secs || 0
      }
    }).filter(entry => entry.avg_secs > 0)

    setModalSeparatorData({
      ...separatorData,
      timeline: separatorTimeline
    })
  }


  const getData = async () => {
    const date = (param: Date): string => {
      return moment(param).format('YYYY-MM-DD')
    }

    try {
      if (controllerRef.current) {
        controllerRef.current?.abort()
      }
      controllerRef.current = new AbortController()

      setLoading(true)

      const response: IGetAverageTimeItemSeparators = await separatorService.getAverageTimeItemSeparators(
        {
          scheduledDateGte: date(scheduledDateGte),
          scheduledDateLte: date(scheduledDateLte),
          separationGroup: selectedGroupId,
          ...(separationMethod && { separationMethod }),
        },
        controllerRef.current,
      )


      // Usar dados globais consolidados
      const globalData = response.global
      const globalUsers = globalData.users.map(user => ({
        username: user.username,
        average_item_separate: user.avg_secs,
        total_items: user.total_items,
        isAboveAverage: user.avg_secs > globalData.avg_secs
      }))

      // Usar toda a timeline (sem filtro por horário)
      const filteredTimeline = response.timeline

      // Dados consolidados
      const filteredData = {
        globalAverage: globalData.avg_secs,
        totalItems: globalData.total_items,
        totalUsers: globalData.users.length,
        totalHours: Number((globalData.total_secs / 3600).toFixed(1)),
        items_per_hour: globalData.items_per_hour || 0,
        users: globalUsers
      }

      // Ordenar por melhor performance (padrão)
      const sortedUsers = [...filteredData.users].sort((a, b) => a.average_item_separate - b.average_item_separate)

      // Calcular média de itens
      const totalUsersWithItems = filteredData.users.filter(user => user.total_items && user.total_items > 0)
      const averageItemsCalculated = totalUsersWithItems.length > 0
        ? totalUsersWithItems.reduce((acc, user) => acc + (user.total_items || 0), 0) / totalUsersWithItems.length
        : 0


      // Atualizar estados
      setMetricsData(filteredData)
      setAverageTimeItem(filteredData.globalAverage)
      setAverageItems(averageItemsCalculated)
      setTimelineData(filteredTimeline)

      setData(sortedUsers)

      // Verificar se há dados válidos (usuários com itens processados)
      const hasValidData = sortedUsers.some(user => user.total_items && user.total_items > 0)
      if (!hasValidData) {
        setNoData(true)
        return
      }
      setNoData(false)
    } catch (err: unknown) {
      const error = err as { message?: string; msg?: string }
      if (error?.message === 'canceled') return
      addPopup({
        title: 'Erro ao pegar os dados de média de separação por itens',
        type: 'error',
        description: error?.message || error?.msg,
      })
    } finally {
      setLoading(false)
    }
  }


  useEffect(() => {
    getData()
  }, [scheduledDateGte, scheduledDateLte, selectedGroupId, separationMethod, refreshTrigger])

  return (
    <Box>
      <LinearProgress variant="indeterminate" style={{ width: '100%', marginBottom: '0.5rem', opacity: loading ? 1 : 0 }} />

      {/* Cards de Métricas */}
      {metricsData && (
        <Box mb={4}>
            <Box
              display="grid"
              style={{
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '1rem',
              }}
            >
              <Card
                elevation={0}
                style={{
                  padding: '24px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #2549CC 0%, #1a3599 100%)',
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography
                      variant="h4"
                      style={{
                        color: '#fff',
                        fontWeight: 800,
                        marginBottom: '8px',
                      }}
                    >
                      {metricsData.globalAverage}s
                    </Typography>
                    <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      Tempo Médio Global por Item
                    </Typography>
                  </Box>
                  <Box
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      borderRadius: '12px',
                      padding: '12px',
                      fontSize: '24px',
                    }}
                  >
                    ⚡
                  </Box>
                </Box>
              </Card>

              <Card
                elevation={0}
                style={{
                  padding: '24px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #89BD23 0%, #6a9419 100%)',
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography
                      variant="h4"
                      style={{
                        color: '#fff',
                        fontWeight: 800,
                        marginBottom: '8px',
                      }}
                    >
                      {metricsData.totalHours}h
                    </Typography>
                    <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      Total de Horas Trabalhadas
                    </Typography>
                  </Box>
                  <Box
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      borderRadius: '12px',
                      padding: '12px',
                      fontSize: '24px',
                    }}
                  >
                    ⏱️
                  </Box>
                </Box>
              </Card>

              <Card
                elevation={0}
                style={{
                  padding: '24px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #6ACFD8 0%, #4ba5ad 100%)',
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography
                      variant="h4"
                      style={{
                        color: '#fff',
                        fontWeight: 800,
                        marginBottom: '8px',
                      }}
                    >
                      {metricsData.totalItems.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      Itens Processados
                    </Typography>
                  </Box>
                  <Box
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      borderRadius: '12px',
                      padding: '12px',
                      fontSize: '24px',
                    }}
                  >
                    📦
                  </Box>
                </Box>
              </Card>

              <Card
                elevation={0}
                style={{
                  padding: '24px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography
                      variant="h4"
                      style={{
                        color: '#fff',
                        fontWeight: 800,
                        marginBottom: '8px',
                      }}
                    >
                      {metricsData.items_per_hour.toFixed(1)} itens/h
                    </Typography>
                    <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      Produtividade Média
                    </Typography>
                  </Box>
                  <Box
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      borderRadius: '12px',
                      padding: '12px',
                      fontSize: '24px',
                    }}
                  >
                    🚀
                  </Box>
                </Box>
              </Card>
            </Box>
        </Box>
      )}

      {/* Alerta de dados vazios */}
      {noData && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          style={{
            height: '300px',
            backgroundColor: '#F9FAFB',
            borderRadius: '12px',
            marginBottom: '1rem',
          }}
        >
          <Typography variant="h6" style={{ color: '#637381' }}>
            Sem dados para o período selecionado
          </Typography>
        </Box>
      )}
      {!noData && metricsData && (
        /* Visão Global */
        <>
          {/* Gráfico de Média por Horário Global */}
          <Box style={{ marginBottom: '1.5rem' }}>
            <Card
              elevation={0}
              style={{
                padding: '1rem',
                border: '1px solid #f0f0f0',
                borderRadius: '16px',
                background: '#fff',
              }}
            >
              <Typography
                variant="h6"
                style={{
                  marginBottom: '1rem',
                  fontWeight: 500,
                  color: '#212B36',
                  paddingLeft: '1.5rem',
                }}
              >
                Média Global por Horário
              </Typography>
              <Box className={classes.scrollChart}>
                <Box style={{ minWidth: '900px' }}>
                  <HourlyAverageChart
                    timelineData={timelineData}
                    title=""
                    onBarClick={setModalHourData}
                  />
                </Box>
              </Box>
            </Card>
          </Box>

          {/* Gráfico de Itens Processados por Hora */}
          <Box style={{ marginBottom: '1.5rem' }}>
            <Card
              elevation={0}
              style={{
                padding: '1rem',
                border: '1px solid #f0f0f0',
                borderRadius: '16px',
                background: '#fff',
              }}
            >
              <Typography
                variant="h6"
                style={{
                  marginBottom: '1rem',
                  fontWeight: 500,
                  color: '#212B36',
                  paddingLeft: '1.5rem',
                }}
              >
                Quantidade de Itens Processados por Hora
              </Typography>
              <Box className={classes.scrollChart}>
                <Box style={{ minWidth: '900px' }}>
                  <ItemsPerHourChart
                    timelineData={timelineData}
                    title=""
                    onBarClick={setModalHourData}
                  />
                </Box>
              </Box>
            </Card>
          </Box>
        </>
      )}

      {/* Visão Global */}
      {!noData && data.length > 0 && (
        <>
          {/* Gráfico Principal - Ranking */}
          <Box style={{ marginBottom: '1.5rem' }}>
            <Card
              elevation={0}
              style={{
                padding: '1rem',
                border: '1px solid #f0f0f0',
                borderRadius: '16px',
                background: '#fff',
              }}
            >
              <Typography
                variant="h6"
                style={{
                  marginBottom: '1rem',
                  fontWeight: 500,
                  color: '#212B36',
                  paddingLeft: '1.5rem',
                }}
              >
                Tempo por Separador
              </Typography>
              <Box className={classes.scrollChart}>
                <Box style={{ minWidth: '900px' }}>
                  <TimePerItem
                    data={data}
                    AverageTimeItem={AverageTimeItem}
                    title=""
                    onBarClick={handleSeparatorClick}
                  />
                </Box>
              </Box>
            </Card>
          </Box>

          {/* Total de Itens por Separador */}
          <Box style={{ marginBottom: '1.5rem' }}>
            <Card
              elevation={0}
              style={{
                padding: '1rem',
                border: '1px solid #f0f0f0',
                borderRadius: '16px',
                background: '#fff',
              }}
            >
              <Typography
                variant="h6"
                style={{
                  marginBottom: '1rem',
                  fontWeight: 500,
                  color: '#212B36',
                  paddingLeft: '1.5rem',
                }}
              >
                Total de Itens por Separador
              </Typography>
              <Box className={classes.scrollChart}>
                <Box style={{ minWidth: '900px' }}>
                  <ItemsPerSeparator
                    data={data}
                    averageItems={averageItems}
                    title=""
                  />
                </Box>
              </Box>
            </Card>
          </Box>
        </>
      )}

      {/* Modal de Detalhes por Horário */}
      <HourDetailModal
        open={!!modalHourData}
        onClose={() => setModalHourData(null)}
        hourData={modalHourData}
      />

      {/* Modal de Detalhes por Separador */}
      <SeparatorDetailModal
        open={!!modalSeparatorData}
        onClose={() => setModalSeparatorData(null)}
        separatorData={modalSeparatorData}
      />
    </Box>
  )
}

export default AverageTimeItem
